import { Injectable, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserColorSchemeService } from '@y42/browser/user-color-scheme';
import { ChatPinsDataHelperService, ChatService } from '@y42/collaboration/chat';
import { Y42RouteDetailTabs, Y42RouterService } from '@y42/feature/routing';
import {
  clearPropertyValueOnModel,
  fromModelChanged,
  GoJsModelProperties,
  isChatRoomsAvatarEvent,
  isInitialLayoutTransactionEvent,
} from '@y42/gojs';
import { DataLineageLink, DataLineageSelectedFilter } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { DiagramEvent, GraphLinksModel, Model, Part, Point, Spot } from 'gojs';
import isEqual from 'lodash/isEqual';
import { ClipboardService } from 'ngx-clipboard';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  share,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { DEFAULT_LINEAGE_SNAPSHOT } from '../../state/data-lineage';
import {
  CLICKABLE_NODE_PARTS,
  DataLineageDiagramQuery,
  DataLineageDiagramUIModel,
  DataLineageModelProperties,
  DataLineageNodeBinding,
  DataLineageNodeTypeToRoute,
  DataLineageUINode,
  LINEAGE_PARTS,
} from '../../state/data-lineage-diagram';
import { DataLineageDiagramStoreService } from '../../state/data-lineage-diagram/data-lineage-diagram-store.service';
import {
  onChangedSelection,
  onSelectionMoved,
  onSubGraphExpandChanged,
} from '../../state/data-lineage-diagram/utils/data-lineage-diagram-event-listeners';
import { DataLineageService } from '../data-lineage/data-lineage.service';
import {
  createDataLineageDiagram,
  filterDataLineageModel,
  lineageDefaultLayout,
  lineageSnapshotLayout,
  setPropertyOnNode,
  setPropertyOnPredecessors,
} from '../gojs';
import { DataLineageDiagramDataService } from './data-lineage-diagram-data.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class DataLineageDiagramService implements OnDestroy {
  constructor(
    private readonly clipboardService: ClipboardService,
    private readonly toastService: ToastService,
    private readonly routerService: Y42RouterService,
    private readonly dataLineageService: DataLineageService,
    private readonly dataLineageDiagramQuery: DataLineageDiagramQuery,
    private readonly dataLineageDiagramStoreService: DataLineageDiagramStoreService,
    private readonly dataLineageDiagramDataService: DataLineageDiagramDataService,
    private readonly chatService: ChatService,
    private readonly chatPinsDataHelperService: ChatPinsDataHelperService,
    private readonly userColorSchemeService: UserColorSchemeService,
  ) {
    this.addDiagramListeners();
    this.snapshotIdListener();
    this.generateDiagramListener();
    this.activeNodeListener();
    this.diagramOutputModelListener();
    this.diagramHighlightsListener();
    this.updateSnapshotListener();
    this.chatAvatarsListener();
  }

  private readonly activeSnapshotId$$ = new BehaviorSubject<string | null>(null);
  readonly activeSnapshotId$ = this.activeSnapshotId$$.pipe(untilDestroyed(this));

  private readonly showNodeTable$$ = new BehaviorSubject<DataLineageUINode | undefined>(undefined);
  readonly showNodeTable$ = this.showNodeTable$$.pipe(untilDestroyed(this));

  private readonly showNodeColumns$$ = new BehaviorSubject<Part | undefined>(undefined);
  readonly showNodeColumns$ = this.showNodeColumns$$.pipe(untilDestroyed(this));

  private readonly diagram$$ = new BehaviorSubject<go.Diagram>(
    createDataLineageDiagram({
      contentAlignment: Spot.TopLeft,
    }),
  );

  private readonly singleChatRoomConfig$ = this.activeSnapshotId$.pipe(
    map((activeSnapshotId) =>
      activeSnapshotId
        ? {
            ...this.chatPinsDataHelperService.getChatCanvasConfigFor(activeSnapshotId),
            relatedGitEntityPath: activeSnapshotId,
          }
        : undefined,
    ),
  );

  private readonly _diagramGenerated$$ = new BehaviorSubject<boolean>(false);
  private readonly modelChanged$ = fromModelChanged(this.diagram).pipe(
    filter((event) => !isInitialLayoutTransactionEvent(event) && !isChatRoomsAvatarEvent(event)),
    share(),
  );

  readonly diagramGenerated$ = this._diagramGenerated$$.pipe(untilDestroyed(this));

  set activeSnapshotId(id: string) {
    this.activeSnapshotId$$.next(id);
  }

  get diagram() {
    return this.diagram$$.value;
  }

  get isDefaultSnapshot() {
    return this.activeSnapshotId$$.value === DEFAULT_LINEAGE_SNAPSHOT;
  }

  private get activeNode() {
    return this.dataLineageDiagramQuery.getValue().activeNode;
  }

  ngOnDestroy(): void {
    this.removeDiagramListener();
  }

  resetPosition(): void {
    this.diagram.position.set(new Point(0, 0));
    this.diagram.redraw();
  }

  private addDiagramListeners(): void {
    this.diagram.addDiagramListener('ObjectSingleClicked', this.objectSingleClicked.bind(this));
    this.diagram.addDiagramListener('SelectionMoved', (e) => onSelectionMoved(e, this.isDefaultSnapshot));
    this.diagram.addDiagramListener('ChangedSelection', onChangedSelection);
    this.diagram.addDiagramListener('SubGraphExpanded', onSubGraphExpandChanged);
    this.diagram.addDiagramListener('SubGraphCollapsed', onSubGraphExpandChanged);
    this.diagram.addDiagramListener('BackgroundSingleClicked', this.onBackgroundSingleClicked.bind(this));
    this.modelChanged$.pipe(untilDestroyed(this)).subscribe();
  }

  private removeDiagramListener(): void {
    this.diagram.removeDiagramListener('ObjectSingleClicked', this.objectSingleClicked.bind(this));
    this.diagram.removeDiagramListener('SelectionMoved', (e) => onSelectionMoved(e, this.isDefaultSnapshot));
    this.diagram.removeDiagramListener('ChangedSelection', onChangedSelection);
    this.diagram.removeDiagramListener('SubGraphExpanded', onSubGraphExpandChanged);
    this.diagram.removeDiagramListener('SubGraphCollapsed', onSubGraphExpandChanged);
    this.diagram.removeDiagramListener('BackgroundSingleClicked', this.onBackgroundSingleClicked.bind(this));
  }

  private snapshotIdListener() {
    this.activeSnapshotId$
      .pipe(
        tap(() => this._diagramGenerated$$.next(false)),
        debounceTime(100),
        filter(Boolean),
        tap(this.setDiagramLayout.bind(this)),
        switchMap((activeSnapshotName) =>
          this.dataLineageDiagramDataService.getLineageModel(activeSnapshotName).pipe(take(1)),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private generateDiagramListener() {
    combineLatest([this.dataLineageDiagramQuery.inputModel$, this.dataLineageDiagramQuery.selectedFilters$])
      .pipe(
        withLatestFrom(this.dataLineageDiagramQuery.outputModel$),
        map(([[inputModel, selectedFilters], outputModel]) => {
          if (!inputModel) {
            return { linkDataArray: [], nodeDataArray: [], visualDataArray: [] };
          }
          const model = this.addVisualNodesIfAvailable(inputModel, outputModel);
          return filterDataLineageModel(model, selectedFilters);
        }),
        filter(Boolean),
        withLatestFrom(this.userColorSchemeService.hasDarkMode$),
        tap(([model, hasDarkMode]) => {
          this.generateDiagram(model, hasDarkMode);
          this.dataLineageDiagramDataService.setSearchNodes(model);
          this._diagramGenerated$$.next(true);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private activeNodeListener(): void {
    this.dataLineageDiagramQuery.activeNode$
      .pipe(
        tap((node) => this.highlightActiveNodeTree(node)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private diagramOutputModelListener(): void {
    this._diagramGenerated$$
      .pipe(
        distinctUntilChanged(),
        filter((diagramGenerated) => !!diagramGenerated),
        switchMap(() => this.modelChanged$),
        debounceTime(1000),
        map(() => this.diagram.model.toJson()),
        filter(Boolean),
        map((modelString) => this.parseModel(modelString)),
        tap((model) => this.setOutputModel(model)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private diagramHighlightsListener(): void {
    this.dataLineageDiagramQuery.selectedHighlights$
      .pipe(
        tap((highlights: DataLineageSelectedFilter[]) => {
          this.dataLineageDiagramStoreService.setActiveNode(null);
          this.diagram.model.set(this.diagram.model.modelData, DataLineageNodeBinding.FilterHighlights, [
            ...highlights,
          ]);
          this.diagram.requestUpdate();
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private updateSnapshotListener(): void {
    this.dataLineageDiagramQuery.outputModel$
      .pipe(
        filter(Boolean),
        distinctUntilChanged(isEqual),
        withLatestFrom(
          this.activeSnapshotId$,
          this.dataLineageDiagramQuery.inputModel$,
          this.dataLineageDiagramQuery.selectedFilters$,
        ),
        filter(([_, snapshotId]) => snapshotId !== DEFAULT_LINEAGE_SNAPSHOT),
        tap(([outputModel, __, inputModel, selectedFilters]) =>
          this.updateSnapshotStore(outputModel, inputModel, selectedFilters),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private chatAvatarsListener() {
    this.singleChatRoomConfig$
      .pipe(
        switchMap((config) =>
          config
            ? combineLatest([this._diagramGenerated$$, this.chatService.getChatRoomsFor(config)]).pipe(
                filter(([diagramGenerated, __]) => !!diagramGenerated),
                tap(async ([__, chatRooms]) => {
                  if (this.diagram) {
                    this.chatPinsDataHelperService.setAvatarsDataInDiagramModelData(chatRooms, this.diagram);
                  }
                }),
              )
            : of(null),
        ),
        catchError(() => {
          return of(null);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private generateDiagram(model: DataLineageDiagramUIModel, hasDarkMode = false) {
    if (!this.diagram) {
      return;
    }

    const diagramModel = Model.fromJson(
      JSON.stringify({
        class: 'go.GraphLinksModel',
        linkDataArray: model.linkDataArray,
        nodeDataArray: [...model.nodeDataArray, ...model.visualDataArray],
      }),
    );
    diagramModel.modelData = {
      [DataLineageModelProperties.ShowNodeDetails]: true,
      [GoJsModelProperties.HasDarkMode]: hasDarkMode,
    };

    this.diagram.model = diagramModel;
  }

  private objectSingleClicked({ subject: { name, part } }: DiagramEvent) {
    const nodeData = part.data;

    if (name === LINEAGE_PARTS.CLIPBOARD_BUTTON) {
      this.copyToClipboard(nodeData);
      return;
    }

    if (name === LINEAGE_PARTS.OVERVIEW_BUTTON) {
      this.redirectToOverview(nodeData);
      return;
    }

    if (name === LINEAGE_PARTS.SHOW_COLUMNS_BUTTON) {
      this.showNodeColumns$$.next(part as Part);
    }

    if (name === LINEAGE_PARTS.SHOW_TABLE_BUTTON) {
      this.showNodeTable$$.next(nodeData);
    }

    if (CLICKABLE_NODE_PARTS.includes(name)) {
      this.clickNode(nodeData);
      return;
    }
  }

  private onBackgroundSingleClicked() {
    this.showNodeColumns$$.next(undefined);
  }

  private copyToClipboard(node: DataLineageUINode): void {
    if (!node.name) {
      return;
    }
    this.clipboardService.copy(node.name);
    this.toastService.success('Copied to Clipboard');
  }

  private async redirectToOverview(node: DataLineageUINode) {
    const redirectionRoute = DataLineageNodeTypeToRoute[node.category];
    const $name = node[DataLineageNodeBinding.$Name];
    if (!redirectionRoute || !$name) {
      return;
    }
    await this.routerService.navigateToDetail(redirectionRoute, $name, Y42RouteDetailTabs.Overview);
  }

  private clickNode(node: DataLineageUINode): void {
    if (!node.name) {
      return;
    }

    this.dataLineageDiagramStoreService.setActiveNode(this.activeNode?.key === node.key ? null : node);
  }

  private parseModel(modelString: string): GraphLinksModel {
    const model = JSON.parse(modelString) as GraphLinksModel;
    return model;
  }

  private highlightActiveNodeTree(node: DataLineageUINode | null) {
    clearPropertyValueOnModel(this.diagram, DataLineageNodeBinding.PathSelection, true);
    clearPropertyValueOnModel(this.diagram, DataLineageNodeBinding.Active, true);
    if (!node) {
      return;
    }

    const currentNode = this.diagram.findNodeForKey(node.key);

    if (currentNode) {
      setPropertyOnNode(currentNode, DataLineageNodeBinding.Active, true);
      setPropertyOnPredecessors(currentNode, DataLineageNodeBinding.PathSelection, true);
    }

    if (node.isGroup) {
      this.diagram?.findNodesByExample({ group: node.key }).each((groupedNode) => {
        if (groupedNode.findNodesInto().count) {
          setPropertyOnPredecessors(groupedNode, DataLineageNodeBinding.PathSelection, true);
        }
      });
    }
  }

  private setOutputModel(model: GraphLinksModel) {
    const visualDataArray = model.nodeDataArray.filter((n) => n.isSidebarType);
    const nodeDataArray = model.nodeDataArray.filter((n) => !n.isSidebarType) as DataLineageUINode[];
    const linkDataArray = model.linkDataArray as DataLineageLink[];
    const outputModel: DataLineageDiagramUIModel = { linkDataArray, nodeDataArray, visualDataArray };
    this.dataLineageDiagramStoreService.setOutputModel(outputModel);
  }

  private updateSnapshotStore(
    outputModel: DataLineageDiagramUIModel,
    inputModel: DataLineageDiagramUIModel | null,
    selectedFilters: DataLineageSelectedFilter[],
  ) {
    if (!inputModel) {
      return;
    }
    const updatedLineageModel = this.dataLineageDiagramDataService.getUpdatedLineageModel(outputModel, inputModel);
    this.dataLineageService.update({ ...updatedLineageModel, selectedFilters });
  }

  private addVisualNodesIfAvailable(
    inputModel: DataLineageDiagramUIModel,
    outputModel: DataLineageDiagramUIModel | null,
  ): DataLineageDiagramUIModel {
    if (!outputModel) {
      return inputModel;
    }

    const { visualDataArray } = outputModel;
    inputModel.visualDataArray = visualDataArray;
    return inputModel;
  }

  private setDiagramLayout(activeSnapshotName: string) {
    this.diagram.layout =
      activeSnapshotName === DEFAULT_LINEAGE_SNAPSHOT ? lineageDefaultLayout() : lineageSnapshotLayout();
  }
}
